import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Check, Droplets, KeyRound, Zap, Clock, MapPin, CreditCard, HelpCircle, ShieldCheck, Timer, Receipt, BadgeCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { buildDeptContent } from "../lib/deptSeoContent";
import { PAYMENTS, REVIEWS } from "../lib/prestations";
import { COMPANY } from "../lib/data";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BrandMarquee from "../components/BrandMarquee";
import GoogleReviews from "../components/GoogleReviews";
import QuoteForm from "../components/QuoteForm";
import Partners from "../components/Partners";
import RecentInterventions from "../components/RecentInterventions";

const ICONS = { Droplets, KeyRound, Zap };

// Rendu simple : convertit une string type "## Titre\n\nParagraphe avec **gras**\n\n- item" en JSX
function renderMarkdownSection(md) {
  const nodes = [];
  const blocks = md.split(/\n\n+/);
  blocks.forEach((block, bi) => {
    if (block.startsWith("## ")) {
      nodes.push(
        <h2 key={`h-${bi}`} className="font-display font-extrabold text-ink text-2xl md:text-3xl mt-10 mb-4">
          {block.replace(/^## /, "")}
        </h2>
      );
    } else if (block.startsWith("- ")) {
      const items = block.split("\n").filter((l) => l.startsWith("- ")).map((l) => l.slice(2));
      nodes.push(
        <ul key={`ul-${bi}`} className="my-4 space-y-2.5">
          {items.map((it, ii) => (
            <li key={ii} className="flex items-start gap-2.5 text-ink-soft leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand shrink-0" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    } else {
      nodes.push(
        <p key={`p-${bi}`} className="text-ink-soft text-base md:text-[17px] leading-relaxed my-4">
          {renderInline(block)}
        </p>
      );
    }
  });
  return nodes;
}

// **gras** -> <strong>
function renderInline(text) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-ink font-semibold">{p}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function DepartmentLandingPage({ metier, deptSlug }) {
  const data = buildDeptContent(deptSlug, metier);
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [metier, deptSlug]);
  useEffect(() => { if (!data) navigate("/"); }, [data, navigate]);
  if (!data) return null;

  const Icon = ICONS[data.metier.icon] || Droplets;
  const path = `/${data.metier.slug}-${deptSlug}`;
  const source = `dept-${deptSlug}-${metier}`;

  return (
    <main data-testid={`dept-${metier}-${deptSlug}`} className="bg-white">
      <Seo path={path} title={data.title.slice(0, 60)} description={data.metaDescription} image={data.metier.image} />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 pb-14 md:pb-20 overflow-hidden bg-slate-50 border-b border-slate-200">
        <div className="pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="chip"><Icon className="h-4 w-4" /> {data.heroChip}</span>
            <h1 className="mt-4 font-display font-extrabold text-ink text-4xl md:text-6xl leading-[1.05]">
              {data.h1} <span className="gradient-text">{data.h1Suffix}</span>
            </h1>
            <p className="mt-5 text-lg text-ink-soft leading-relaxed max-w-xl">
              {renderInline(data.intro)}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={COMPANY.phoneHref} data-testid="dept-call-btn" className="btn-primary text-base">
                <Phone className="h-5 w-5" /> {COMPANY.phone}
              </a>
              <a href="#devis" className="btn-outline">Devis gratuit</a>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {["Intervention 24h/24 & 7j/7", "Devis gratuit & sans surprise", "Artisans certifiés & assurés", `Département ${data.dept.code}`].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-ink-soft font-medium">
                  <span className="h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                    <Check className="h-3 w-3 text-brand-green" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[4/3]"
          >
            <img src={data.metier.image} alt={`${data.metier.label} ${data.dept.label}`} fetchPriority="high" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <BrandMarquee category={data.metier.category} />

      {/* Contenu éditorial SEO + form sticky */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8">
            {data.sections.map((s) => (
              <Reveal key={s.id}>
                <div>{renderMarkdownSection(s.md)}</div>
              </Reveal>
            ))}

            {/* Engagements */}
            <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl mt-12 mb-6">Nos engagements</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.engagements.map((e, i) => (
                <div key={i} className="card-soft p-5 hover:translate-y-0">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                      {i === 0 && <Receipt className="h-4 w-4 text-brand" />}
                      {i === 1 && <ShieldCheck className="h-4 w-4 text-brand" />}
                      {i === 2 && <Clock className="h-4 w-4 text-brand" />}
                      {i === 3 && <Timer className="h-4 w-4 text-brand" />}
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-ink">{e.t}</h3>
                      <p className="text-ink-soft text-sm mt-1">{e.d}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Villes chips */}
            <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl mt-12 mb-5 flex items-center gap-2">
              <MapPin className="h-6 w-6 text-brand" /> Villes desservies {data.dept.prep}
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.dept.cities.map((c) => (<span key={c} className="chip">{c}</span>))}
            </div>

            {/* Mots-clés cachés = keywords SEO */}
            <p className="sr-only">
              Mots-clés : {data.keywords.join(", ")}, {data.metier.label} {data.dept.label},
              {data.metier.label} {data.dept.cities[0]}, {data.metier.label} {data.dept.cities[1]},
              dépannage {data.metier.category} {data.dept.name}, urgence {data.metier.category} {data.dept.code}.
            </p>
          </article>

          {/* Sidebar sticky */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="card-soft p-7 hover:translate-y-0 text-center">
                <p className="overline">Appelez immédiatement</p>
                <a href={COMPANY.phoneHref} className="block font-display font-extrabold text-ink text-3xl mt-2 hover:text-brand transition-colors">{COMPANY.phone}</a>
                <a href={COMPANY.phoneHref} className="btn-primary w-full mt-4"><Phone className="h-4 w-4" /> Appeler maintenant</a>
              </div>
              <div id="devis" className="card-soft p-6 md:p-7 hover:translate-y-0 scroll-mt-24">
                <h3 className="font-display font-bold text-ink text-xl mb-4">Demander un devis gratuit</h3>
                <QuoteForm defaultService={data.metier.category} source={source} />
              </div>
              <div className="card-soft p-6 hover:translate-y-0">
                <h3 className="font-display font-bold text-ink text-lg flex items-center gap-2"><CreditCard className="h-5 w-5 text-brand" /> Moyens de paiement</h3>
                <div className="flex flex-wrap gap-2 mt-3">{PAYMENTS.map((p) => (<span key={p} className="chip">{p}</span>))}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Avis */}
      <div className="bg-slate-50 border-y border-slate-200">
        <GoogleReviews reviews={REVIEWS[data.metier.category]} title="Avis de nos clients en Île-de-France" subtitle="Des interventions notées 5 étoiles." />
      </div>

      {/* Map */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="rounded-2xl overflow-hidden border border-slate-200 min-h-[320px]">
            <iframe title={data.dept.name} src={data.dept.mapEmbed} width="100%" height="100%" style={{ border: 0, minHeight: "320px", display: "block" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          <div className="grid sm:grid-cols-1 gap-5">
            <div className="card-soft p-6 hover:translate-y-0">
              <span className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center"><Clock className="h-5 w-5 text-brand" /></span>
              <h3 className="font-display font-bold text-ink text-lg mt-3">Horaires d&apos;intervention {data.dept.prep}</h3>
              <p className="text-ink-soft">24 h/24 et 7 j/7 — jours fériés compris. Un {data.metier.labelLower} est toujours d&apos;astreinte {data.dept.prep}.</p>
            </div>
            <div className="card-soft p-6 hover:translate-y-0">
              <span className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center"><BadgeCheck className="h-5 w-5 text-brand" /></span>
              <h3 className="font-display font-bold text-ink text-lg mt-3">Nos garanties</h3>
              <p className="text-ink-soft">Devis gratuit, tarif transparent, artisans certifiés & assurés, travail garanti pièces et main-d&apos;œuvre.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="text-center font-display font-extrabold text-ink text-3xl md:text-4xl flex items-center justify-center gap-2">
            <HelpCircle className="h-7 w-7 text-brand" /> Questions fréquentes {data.dept.prep}
          </h2>
          <Accordion type="single" collapsible className="w-full mt-8 space-y-3">
            {data.faq.map((item, i) => (
              <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-item-${i}`} className="card-soft border border-slate-200 px-6 hover:translate-y-0">
                <AccordionTrigger className="font-display font-bold text-ink text-base md:text-lg text-left hover:no-underline py-5 [&>svg]:text-brand">{item.q}</AccordionTrigger>
                <AccordionContent className="text-ink-soft text-base leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <a href={COMPANY.phoneHref} className="btn-primary text-base"><Phone className="h-5 w-5" /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      {/* Actualités : dernières interventions locales */}
      <RecentInterventions metierKey={data.metier.category} dept={data.dept} />

      {/* Partenaires B2B — crédibilité syndics/agences */}
      <Partners deptLabel={data.dept.prep} />

      {/* Internal linking : autres départements */}
      <section className="py-14 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <h2 className="font-display font-extrabold text-ink text-xl md:text-2xl mb-5 text-center">
            {data.metier.label} dans les autres départements d&apos;Île-de-France
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {["paris", "yvelines", "essonne", "seine-et-marne", "hauts-de-seine", "seine-saint-denis", "val-de-marne", "val-d-oise"]
              .filter((s) => s !== deptSlug)
              .map((s) => (
                <Link
                  key={s}
                  to={`/${data.metier.slug}-${s}`}
                  className="chip hover:bg-brand hover:text-white transition-colors"
                >
                  {data.metier.label} {s.replace(/-/g, " ")}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}
